import { CoinsUsdtIcon } from '@/client/assets';
import { ThemeMode } from '@/client/hooks/use-theme';
import { APPBaseTokenConfig } from '@/client/types/app';
import { findByAddress } from '@/client/utils/address-match';
import { SupportedLanguage } from '@/libs/sdk/api';
import { Chain, baseSepolia, bsc, bscTestnet } from 'viem/chains';

export const IS_DEV = import.meta.env.DEV;
export const IS_PROD = import.meta.env.PROD;
export const IS_PREVIEW = import.meta.env.MODE === 'preview';
export const IS_DEBUG = IS_DEV || IS_PREVIEW || import.meta.env.VITE_DEBUG === 'true';

export const APP_I18N: {
  defaultLocale: SupportedLanguage;
  version: string;
  onlineVersion: string;
  locales: { name: string; lang: SupportedLanguage }[];
} = {
  defaultLocale: 'en',
  // bump when public/lang/* strings change — it busts the ?v= query cache
  version: '1.1.12',
  onlineVersion: '1.0.5',
  locales: [
    { name: 'English', lang: 'en' },
    { name: 'العربية', lang: 'ar' },
    { name: '简体中文', lang: 'zh-cn' },
    { name: '繁體中文', lang: 'zh-hk' },
    { name: 'Español', lang: 'es' },
    { name: '日本語', lang: 'ja' },
    { name: 'Tiếng Việt', lang: 'vi' },
    { name: '한국어', lang: 'ko' },
    { name: 'Русский', lang: 'ru' },
    { name: 'Português', lang: 'pt' },
  ],
};
export const APP_THEME_MODE: ThemeMode = 'dark'; // light | dark | auto -- AIP brand guide: dark mode primary

// Settlement-deadline display zone (per-app config; consumed by market-deadline.tsx,
// which re-exports it as DEADLINE_TZ/DEADLINE_FMT). AIP renders deadlines in Dubai
// time (GST, UTC+4, no DST) to match the AIP market rules. This is intentionally an
// app-config value, NOT a hardcoded default in the shared component: the DID build
// lives on its own branch and MUST keep SGT. If market-deadline.tsx is ever
// cherry-picked to the DID branch, that branch's app.ts must supply
// DEADLINE_TIMEZONE = 'Asia/Singapore' / DEADLINE_TZ_LABEL = 'SGT'.
export const DEADLINE_TIMEZONE = 'Asia/Dubai';
export const DEADLINE_TZ_LABEL = 'GST';

// Default landing tab of the home Browse list. 'trending' is the first tab and,
// because the AIP tenant runs market-service with min_volume=0, its
// minVolumeTrending filter returns ALL live markets (not just high-volume ones),
// so it never opens empty. (Earlier we pinned a category UUID — World Cup, then
// Crypto — but a category tab goes blank once its markets settle; 'trending'
// doesn't have that failure mode.)
export const DEFAULT_HOME_CATEGORY = 'trending';

// Home "Trending now" rail: curated pins from this admin section (get-sections)
// take priority over the volume-sorted fallback. Matched by id first; title is
// a fallback for id drift across environments (id is prod-verified, not
// guaranteed stable elsewhere).
// Empty id never matches a real section; matching falls back to the title below,
// and with no section the rail uses the volume-sorted fallback (never empty).
export const TRENDING_RAIL_SECTION_ID = '421b4b9f-33fb-4e3a-8549-ebe3b123ed56';
export const TRENDING_RAIL_SECTION_TITLE = 'Trending';

export const TERMS_OF_USE_LINK = '/pdf?file=terms-of-use';
export const RISK_DISCLOSURE_LINK = '/pdf?file=risk-disclosure';
// TODO(saker): official SakerX channels unknown — placeholders until provided.
export const TELEGRAM_LINK = '#';
export const DISCORD_LINK = '#';
export const TWITTER_LINK = '#';
export const YOUTUBE_LINK = '#';
export const WEBSITE_LINK = '#';
// TODO(saker): SakerX docs — '#' until provided; the old value leaked the Promethex gitbook.
export const HELP_LINK = '#';
export const PRIVACY_LINK = '/pdf?file=privacy';
export const STATUS_LINK = '/';
export const FEEDBACK_LINK = '#'; // TODO(saker): SakerX contact
export const MEDIUM_LINK = '#';

// '#' placeholders above are hidden in the UI until the real SakerX channels
// arrive — render sites gate on this instead of shipping dead links.
export const isRealLink = (href: string) => href !== '' && href !== '#';

// pdf files. Source of truth for the text lives in the vault
// (clients/sakerx/legal/) — regenerate the PDF and bump TERMS_VERSION on any
// change; never edit a shipped PDF in place (filenames are versioned).
export const PDF_FILES = {
  privacy: '/pdfs/saker-privacy-policy-1.0.pdf',
  'terms-of-use': '/pdfs/saker-terms-of-use-1.1.pdf',
  'risk-disclosure': '/pdfs/saker-risk-disclosure-1.0.pdf',
} as const;

// open slippage
// Manual max-slippage control (APMM-style). When true, TradeSettings shows the
// slippage input and trade-confirm uses the user's value as the min-receive buffer
// (floored at 0.5%); when false it stays hidden and min-receive falls back to the
// price-impact auto-buffer. Kept as one switch so the control and its wiring never
// drift (a shown-but-unwired control silently ignores the user's slippage).
export const OPEN_SLIPPAGE = true;

// refetchInterval
export const REFETCH_INTERVAL_FAST = 5 * 1e3;
export const REFETCH_INTERVAL_MEDIUM = 10 * 1e3;
export const REFETCH_INTERVAL_SLOW = 15 * 1e3;

// default img
export const DEFAULT_YSE_IMG = '/img/yes.png';
export const DEFAULT_NO_IMG = '/img/no.png';
export const DEFAULT_UP_IMG = '/img/up.png';
export const DEFAULT_DOWN_IMG = '/img/down.png';

// Test market addresses for development
// Market 1: In Progress (before settlement) - Binary
export const TEST_MARKET_ADDRESS = '0x0000000000000000000000000000000000SOL001';
// Market 2: Disputed (in dispute period)
export const TEST_MARKET_ADDRESS_DISPUTED = '0x0000000000000000000000000000000000SOL002';
// Market 3: Closed (final result - YES wins)
export const TEST_MARKET_ADDRESS_CLOSED = '0x0000000000000000000000000000000000SOL003';
// Market 4: Multi-option market (6 options)
export const TEST_MARKET_ADDRESS_MULTI = '0x0000000000000000000000000000MULTI001';

// Array of test markets for easy access by index
export const TEST_MARKET_ADDRESSES = [
  TEST_MARKET_ADDRESS, // index 0: In Progress (Binary)
  TEST_MARKET_ADDRESS_DISPUTED, // index 1: Disputed
  TEST_MARKET_ADDRESS_CLOSED, // index 2: Closed
  TEST_MARKET_ADDRESS_MULTI, // index 3: Multi-option
];

// analytics tokens
export const CLOUDFLARE_ANALYTICS_TOKEN = import.meta.env.VITE_CLOUDFLARE_ANALYTICS_TOKEN;
export const GOOGLE_ANALYTICS_TOKEN = import.meta.env.VITE_GOOGLE_ANALYTICS_TOKEN;
export const UMAMI_ANALYTICS_URL = import.meta.env.VITE_UMAMI_ANALYTICS_URL;
export const UMAMI_ANALYTICS_TOKEN = import.meta.env.VITE_UMAMI_ANALYTICS_TOKEN;
export const AHREFS_ANALYTICS_TOKEN = import.meta.env.VITE_AHREFS_ANALYTICS_TOKEN;

// sentry
export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN?.replace('[host]', window.location.host);

export const BASE_TOKEN_CONFIG: APPBaseTokenConfig[] = [
  {
    // SakerX beta = SINGLE collateral (testnet USDT, 18 decimals). The second AIP
    // entry was removed for this single-token tenant: with one entry the currency
    // switcher (BASE_TOKEN_CONFIG.length < 2) hides itself, so no second currency
    // renders. Real-backend builds inject the testnet USDT address + 18 decimals via
    // VITE_BASE_TOKEN_*; this MUST match the backend's get-base-token-config or
    // get-markets 400s. The literal below is only the no-env dev fallback.
    address: import.meta.env.VITE_BASE_TOKEN_ADDRESS || '0x1A8da4723A4f7aD0A965cb3dBC31c72435b1B490',
    icon: CoinsUsdtIcon,
    assetsIcon: CoinsUsdtIcon,
    img: '/img/bg-card-usdc.png',
    translateKey: 'usdt',
    tooltipKey: 'usdt-trading-mode',
    name: import.meta.env.VITE_BASE_TOKEN_NAME || 'USDT',
    symbol: import.meta.env.VITE_BASE_TOKEN_SYMBOL || 'USDT',
    decimals: Number(import.meta.env.VITE_BASE_TOKEN_DECIMALS) || 18,
  },
];

// Global currency lens default = the single SakerX collateral (testnet USDT).
// First-visit lens default: a visitor with no persisted choice starts here.
// Returning visitors keep their own persisted lens — nothing resets it on load.
// (Per-market collateral is unaffected — that comes from each market's
// baseTokenAddress, not this constant.)
export const DEFAULT_BASE_TOKEN = BASE_TOKEN_CONFIG[0];

// Resolve a persisted base-token address back to the live config entry —
// config objects carry icon component functions that JSON persistence drops.
export const resolveBaseToken = (address?: string) => findByAddress(BASE_TOKEN_CONFIG, address);

// Legacy AIP exports kept for import stability; unused by the SakerX single-token
// tenant. AIP_TOKEN_ADDRESS points at the single collateral so it never reads a
// missing [1] entry. TODO(saker): SakerX has no buy/stake app — links are placeholders.
export const AIP_TOKEN_ADDRESS = BASE_TOKEN_CONFIG[0].address;
export const AIP_BUY_LINK = '#';
export const AIP_STAKE_LINK = '#';

/// chain config

// Feature flags
export const CLOB_ENABLED = import.meta.env.VITE_CLOB_ENABLED !== 'false';
// On-demand machine translation of market copy. Off per-tenant (e.g. AIP ships
// pre-localized content). When off the client MUST NOT call api.base.translate —
// that endpoint 503s here and spams the console. The env var existed but nothing
// read it, so the flag was a no-op; this constant makes it real.
export const API_TRANSLATION_DISABLED = import.meta.env.VITE_DISABLE_API_TRANSLATION === 'true';
// Referral fee-share (ADR-048). Gates the invite-earn page + its nav entry.
// Replaces the legacy VITE_REWARDS_ENABLED points/tasks flag (the page moved from
// points to referral earnings, so that flag now gates nothing). Opt-in per tenant
// during rollout: AIP prod sets VITE_REFERRAL_ENABLED=true.
export const REFERRAL_ENABLED = import.meta.env.VITE_REFERRAL_ENABLED === 'true';
// Claim stays dark until the audited Merkle distributor is deployed. Config-gated,
// NEVER inferred from claimable>0 — wiring a claim to a placeholder distributor is a
// fund-loss footgun (ADR-048). MERKLE_DISTRIBUTOR is empty until that contract ships.
export const REFERRAL_CLAIM_ENABLED = import.meta.env.VITE_REFERRAL_CLAIM_ENABLED === 'true';
export const MERKLE_DISTRIBUTOR = (import.meta.env.VITE_MERKLE_DISTRIBUTOR || '') as string;

// White-label UID display prefix. The backend mints uids as "prome<n>"; show a
// tenant brand prefix in the UI instead when configured. Display-only — routing
// and API calls still use the raw uid.
export const UID_DISPLAY_PREFIX = import.meta.env.VITE_UID_DISPLAY_PREFIX || '';
export const displayUid = (uid?: string): string => {
  if (!uid) return '';
  return UID_DISPLAY_PREFIX && uid.startsWith('prome') ? UID_DISPLAY_PREFIX + uid.slice(5) : uid;
};

// Reown AppKit
export const REOWN_PROJECT_ID = import.meta.env.VITE_REOWN_PROJECT_ID || '';

// Chain config — driven by VITE_CHAIN_ID: 56 = BSC mainnet (AIP), 97 = BSC testnet
// (SakerX beta), anything else = Base Sepolia (dev default). Without the explicit 97
// branch, testnet builds silently fell through to Base Sepolia -> wrong-chain wallet
// + dead RPC.
const CHAIN_ID = Number(import.meta.env.VITE_CHAIN_ID || baseSepolia.id);
const BASE_CHAIN: Chain = CHAIN_ID === bsc.id ? bsc : CHAIN_ID === bscTestnet.id ? bscTestnet : baseSepolia;
const FALLBACK_RPCS =
  CHAIN_ID === bsc.id
    ? ['https://bsc-rpc.publicnode.com', 'https://bsc-dataseed.binance.org']
    : CHAIN_ID === bscTestnet.id
      ? [
          'https://bsc-testnet-rpc.publicnode.com',
          'https://bsc-testnet.blockpi.network/v1/rpc/public',
          'https://data-seed-prebsc-1-s1.binance.org:8545',
        ]
      : ['https://base-sepolia-rpc.publicnode.com', 'https://base-sepolia.blockpi.network/v1/rpc/public'];
// Dedupe: the env default usually repeats one of the fallbacks, and a
// duplicated dead RPC doubles the failover delay.
export const RPC_URLS = [
  ...new Set([import.meta.env.VITE_DEFAULT_RPC_URL || BASE_CHAIN.rpcUrls.default.http[0], ...FALLBACK_RPCS]),
];
export const RPC_URL = RPC_URLS[0];
export const APP_CHAIN: Chain = {
  ...BASE_CHAIN,
  rpcUrls: {
    default: {
      http: RPC_URLS,
    },
  },
};

// Testnet faucet (beta only): the TestToken collateral exposes a public
// faucet(amount) self-mint, so the claim button is a plain wallet write. tBNB
// for gas cannot be self-served — the team funds wallets directly (see
// prome-admin/scripts/saker-fund-wallets.sh). Gated on the testnet chain id so
// a future mainnet build drops the whole feature without a code change.
export const FAUCET_ENABLED = APP_CHAIN.id === bscTestnet.id;
// Whole tokens per claim, scaled by the base token's decimals at call time.
export const FAUCET_CLAIM_AMOUNT = 1000;

// Deposit network label shown to users. AIP launches on BNB Smart Chain (USDT);
// APP_CHAIN above is still the testnet config used by the mock/wallet layer, so
// the user-facing deposit copy uses this explicit label instead of APP_CHAIN.name.
// TODO(aip): drive from tenant config alongside the real BSC chain + USDT address.
export const DEPOSIT_NETWORK_LABEL = 'BNB Smart Chain';

// Contract addresses. CTF (ConditionalTokens, used by claim/redeem) is env-driven
// so AIP/BSC reads VITE_CTF_ADDRESS; CLOB-only addresses stay testnet (CLOB disabled).
export const APP_CONTRACTS = {
  CTF: (import.meta.env.VITE_CTF_ADDRESS || '0x6b5b67b6C620A035675807bcCD49BA8D26ebffa3') as `0x${string}`,
  CTF_EXCHANGE: '0x53eeC832155a178D08Ab6B23cFF13c2321239655' as `0x${string}`,
  NEG_RISK_REGISTRY: '0xb2304E3397Dd29645C53486471051A32232D76cB' as `0x${string}`,
  tUSDC: '0x1A8da4723A4f7aD0A965cb3dBC31c72435b1B490' as `0x${string}`,
};

// CLOB API
// In dev mode, use the Vite proxy to avoid CORS (HTTPS page → HTTP API).
// In production, use the configured URL directly.
export const CLOB_API_URL = IS_DEV ? '/clob-api' : import.meta.env.VITE_CLOB_API_URL || '';
export const CLOB_WS_URL = import.meta.env.VITE_CLOB_WS_URL || '';
// Gate ALL CLOB queries on this, not CLOB_ENABLED alone: the flag defaults to
// true when VITE_CLOB_ENABLED is unset, so a tenant env with no CLOB vars at
// all would otherwise mount every query into a permanent throw/retry loop
// against the disabled client (zero network, but endless churn).
export const CLOB_READY = CLOB_ENABLED && !!CLOB_API_URL;

// CLOB exchange contract — used as the EIP-712 verifyingContract for Order signing.
export const CLOB_EXCHANGE_CONTRACT = (import.meta.env.VITE_CLOB_EXCHANGE_CONTRACT ||
  APP_CONTRACTS.CTF_EXCHANGE) as `0x${string}`;

// EIP-712 domain for CLOB Order signing.
export const CLOB_EIP712_DOMAIN_NAME = import.meta.env.VITE_CLOB_EIP712_DOMAIN_NAME || 'CTFSettlement';
export const CLOB_EIP712_DOMAIN_VERSION = import.meta.env.VITE_CLOB_EIP712_DOMAIN_VERSION || '1';

// gas config (Base L2)
export const GAS_CONFIG = {
  maxPriorityFeePerGas: 1500000n,
  maxFeePerGas: 1000000000n,
};
